import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { cn } from "design-system/utils"
import { useUpdateConversationTicketStatusMutation } from "modules/support/hooks/useUpdateConversationTicketStatusMutation"
import { ComponentProps } from "react"
import { useTranslation } from "react-i18next"
import { TicketStatusEnum } from "schema"
import { toast } from "sonner"

type Props = {
  ticket: {
    id: string
    status: keyof typeof TicketStatusEnum
  }
  size?: ComponentProps<typeof SelectTrigger>["size"]
  className?: string
}

export function TicketStatusDropdown({ ticket, className, size }: Props) {
  const { t } = useTranslation()

  const statusCopies = {
    Pending: t("support.tickets.status.pending"),
    PartiallyResolved: t("support.tickets.status.partiallyResolved"),
    Resolved: t("support.tickets.status.resolved"),
    Closed: t("support.tickets.status.closed"),
  }

  const { mutateAsync: updateStatus } = useUpdateConversationTicketStatusMutation(ticket.id)

  async function onUpdateStatus(status: keyof typeof TicketStatusEnum) {
    const previousStatus = ticket.status

    await updateStatus(status)

    function onUndo() {
      updateStatus(previousStatus)
    }

    toast.success(t("support.tickets.status.updated"), {
      action: {
        label: t("shared.undo"),
        onClick: onUndo,
      },
    })
  }

  return (
    <div className={cn("flex flex-row gap-2", className)}>
      <Select
        value={ticket.status}
        onValueChange={(value) => {
          onUpdateStatus(value as keyof typeof TicketStatusEnum)
        }}
      >
        <SelectTrigger className="max-w-48" size={size}>
          <SelectValue>{statusCopies[ticket.status]}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {Object.keys(TicketStatusEnum).map((key) => (
            <SelectItem key={key} value={key}>
              {statusCopies[key as keyof typeof TicketStatusEnum]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
